import { ExecutionResult, GraphQLError } from 'graphql';
import _ from 'lodash';
import MIMEType from 'whatwg-mimetype';
import { Subscription } from 'zen-observable-ts';

import { headersWithContentType } from 'src/app/graph/explorerPage/helpers/headersWithContentType';
import {
  JSONObject,
  JSONValue,
} from 'src/app/graph/explorerPage/helpers/types';
import { isMinLengthArray } from 'src/lib/isMinLengthArray';

import { QueryPlanResponse } from '../../../helpers/queryPlanHelpers';
import { ConnectorsDebuggingResult } from '../../../resultsPane/connectorsResponsePane/shared';
import { FileVariable } from '../useEditorTabState/useEditorTabState';

import { constructMultipartForm } from './constructMultipartForm';
import { readMultipartWebStream } from './readMultipartWebStream';

export const TRACE_KEY = 'ftv1';
export const QUERY_PLAN_KEY = 'apolloQueryPlan';
export const CONNECTORS_DEBUGGING_HEADER = 'Apollo-Connectors-Debugging';
export const CONNECTORS_DEBUGGING_KEY = 'apolloConnectorsDebugging';
export const DEFER_ACCEPT_HEADER =
  'multipart/mixed; deferSpec=20220824, application/json';
export const MULTI_PART_SUB_ACCEPT_HEADER =
  'multipart/mixed; boundary="graphql"; subscriptionSpec=1.0, application/json';

export function processOperationHeaders(
  headers: Record<string, string>,
  operationString: string,
  isMultipartSubscription: boolean,
  addContentType: boolean,
) {
  const processedHeaders = addContentType
    ? headersWithContentType(headers)
    : { ...headers };
  const userDefinedAcceptsHeader = processedHeaders.accept;

  // defer requires the accepts header to be sent to work
  const hasDefer = operationString.match(/@defer\b/);
  if (hasDefer) {
    if (
      userDefinedAcceptsHeader &&
      userDefinedAcceptsHeader !== DEFER_ACCEPT_HEADER
    ) {
      // eslint-disable-next-line no-console
      console.warn(
        `This operation includes defer and therefore must send the accepts header with the value "${DEFER_ACCEPT_HEADER}", but a user defined value of "${userDefinedAcceptsHeader}" was found. Please remove this header and explorer will add the required value.`,
      );
    } else {
      processedHeaders.accept = DEFER_ACCEPT_HEADER;
    }
  }

  // TODO defer support in multi part subs is currently not supported, but may
  // be in the future. We need to know the correct accept header that needs to
  // be sent for a valid operation
  if (isMultipartSubscription) {
    if (
      userDefinedAcceptsHeader &&
      userDefinedAcceptsHeader !== MULTI_PART_SUB_ACCEPT_HEADER
    ) {
      // eslint-disable-next-line no-console
      console.warn(
        `This operation is a Multipart subscription and therefore must send the accepts header with the value "${MULTI_PART_SUB_ACCEPT_HEADER}", but a user defined value of "${userDefinedAcceptsHeader}" was found. Please remove this header and explorer will add the required value.`,
      );
    } else {
      processedHeaders.accept = MULTI_PART_SUB_ACCEPT_HEADER;
    }
  }

  return processedHeaders;
}

export const isPotentialCORSError = (error: Error) =>
  (error instanceof TypeError || error.name === 'TypeError') &&
  !/failed to execute/i.exec(error.message);

export interface OperationRequestBody {
  query: string;
  variables: JSONObject | null;
  operationName: string | undefined;
}

interface TimelineResponse {
  timestamp: number;
  data: ExecutionResult<JSONObject, Extensions>;
}
export interface ResponseAndMetrics {
  type: 'result';
  body?: ExecutionResult<JSONObject>;
  trace?: string | null;
  queryPlan: QueryPlanResponse | null | undefined;
  bodyText: string;
  headers: Array<Record<string, string>>;
  status: number;
  size: number;
  hasNext: boolean;
  history?: TimelineResponse[];
  startTime: number;
  endTime: number;
  connectorsDebugging?: ConnectorsDebuggingResult | null;
}

export interface CancelledResponse {
  type: 'cancelled';
}

export interface ResponseUpdatedInCallback {
  type: 'response-updated-in-callback';
  status: number;
}

export interface Extensions {
  [TRACE_KEY]?: string;
  [QUERY_PLAN_KEY]?: QueryPlanResponse;
  [CONNECTORS_DEBUGGING_KEY]?: ConnectorsDebuggingResult;
}

export interface ResponseData {
  data: Record<string, unknown> | JSONValue | undefined;
  path?: Array<string | number>;
  errors?: Array<GraphQLError>;
  extensions?: Extensions;
}

// https://github.com/apollographql/router-private/blob/subscriptions/dev-docs/multipart-subscriptions-protocol.md
export interface MultipartSubscriptionResponse {
  data: {
    errors?: Array<GraphQLError>;
    payload:
      | (ResponseData & {
          error?: { message: string; stack?: string };
        })
      | null
      | undefined;
  };
  headers?: Record<string, string> | Record<string, string>[];
  size: number;
  status?: number;
}

export interface MultipartResponse {
  data: ResponseData & {
    incremental?: Array<
      ResponseData & { path: NonNullable<ResponseData['path']> }
    >;
    error?: { message: string; stack?: string };
    hasNext?: boolean;
  };
  headers?: Record<string, string> | Record<string, string>[];
  size: number;
}

interface CurrentResponse {
  body: {
    data?: JSONObject | null;
    // only exists for multipart subs
    errors?: GraphQLError[];
    error?: { message: string; stack?: string };
    extensions?: Extensions;
  };
  trace?: string | null;
  queryPlan: QueryPlanResponse | null | undefined;
  size: number;
  headers: Array<Record<string, string>>;
  history: TimelineResponse[];
  connectorsDebugging?: ConnectorsDebuggingResult | null;
}
export const DEFAULT_CURRENT_RESPONSE: CurrentResponse = {
  body: {},
  size: 0,
  headers: [],
  history: [],
  queryPlan: undefined,
};

function buildResponse({
  body,
  startTime,
  bodyText,
  fallbackSize,
  response,
  hasNext,
  deferHeaders,
  history,
  trace,
  queryPlan,
  connectorsDebugging,
}: {
  bodyText: undefined | string;
  fallbackSize: number;
  response: Response;
  deferHeaders?: Record<string, string>[];
} & Pick<
  ResponseAndMetrics,
  | 'trace'
  | 'queryPlan'
  | 'body'
  | 'startTime'
  | 'hasNext'
  | 'history'
  | 'connectorsDebugging'
>): ResponseAndMetrics {
  const endTime = Date.now();

  const contentLength = response.headers.get('content-length');
  const size = contentLength ? parseInt(contentLength, 10) : fallbackSize;

  return {
    type: 'result',
    body,
    headers: [
      Object.fromEntries(response.headers.entries()),
      ...(deferHeaders ?? []),
    ],
    status: response.status,
    size,
    hasNext,
    history,
    startTime,
    endTime,
    trace,
    queryPlan,
    connectorsDebugging,
    get bodyText() {
      return bodyText ?? JSON.stringify(body);
    },
  };
}

export function updateMultipartResponse({
  currentResponse,
  newResponse,
  extractExtensions,
  shouldTerminate,
  pushToHistory = true,
}: {
  currentResponse: CurrentResponse;
  newResponse: MultipartResponse | MultipartSubscriptionResponse;
  extractExtensions: ReturnType<
    typeof handleApolloHeadersAndExtraction
  >['extractExtensions'];
  shouldTerminate: boolean;
  pushToHistory?: boolean;
}): CurrentResponse {
  const newResponseData = newResponse.data;
  const unwrappedSubscriptionOrDeferResponseData:
    | MultipartResponse['data']
    | null
    | undefined =
    'payload' in newResponseData
      ? // payload is potentially null if we get transport layer termination from the server
        newResponseData.payload
      : newResponseData;

  let updatedResponse: CurrentResponse = {
    ...currentResponse,
    body: {
      ...currentResponse.body,
      // Ensure key order in body is as close of a match to actual data as possible
      ...Object.fromEntries(
        Object.keys(unwrappedSubscriptionOrDeferResponseData ?? newResponseData)
          // filter out existing keys, we want to do this instead of putting it before the current spread
          // so that once a key is added it maintains it's order, and keys that come later are added to the bottom
          .filter((key) => !(key in currentResponse.body))
          .map((key) => [key, undefined]),
      ),
    },
    headers: currentResponse.headers.concat(newResponse.headers ?? []),
    size: currentResponse.size + newResponse.size,
  };

  const payload = 'payload' in newResponseData ? newResponseData.payload : null;
  if (newResponseData.errors || payload?.errors) {
    updatedResponse.body.errors = [
      ...(updatedResponse.body.errors ?? []),
      ...(newResponseData.errors ?? []),
      ...(payload?.errors ?? []),
    ];
  }

  if (unwrappedSubscriptionOrDeferResponseData?.error) {
    updatedResponse.body.error = unwrappedSubscriptionOrDeferResponseData.error;
  }

  if (unwrappedSubscriptionOrDeferResponseData?.extensions) {
    updatedResponse.body.extensions = {
      ...updatedResponse.body.extensions,
      ...unwrappedSubscriptionOrDeferResponseData.extensions,
    };
    const { queryPlan, trace, connectorsDebugging } =
      extractExtensions(updatedResponse);
    updatedResponse.trace = trace ?? updatedResponse.trace;
    updatedResponse.queryPlan = queryPlan ?? updatedResponse.queryPlan;
    updatedResponse.connectorsDebugging =
      connectorsDebugging ?? updatedResponse.connectorsDebugging;
  }

  if (
    unwrappedSubscriptionOrDeferResponseData &&
    isMinLengthArray(1, unwrappedSubscriptionOrDeferResponseData.path)
  ) {
    const existing = _.get(
      updatedResponse.body.data,
      unwrappedSubscriptionOrDeferResponseData.path,
    );
    updatedResponse.body.data = _.setWith(
      _.clone(updatedResponse.body.data ?? {}),
      unwrappedSubscriptionOrDeferResponseData.path,
      _.isNil(existing)
        ? unwrappedSubscriptionOrDeferResponseData.data
        : _.merge({}, existing, unwrappedSubscriptionOrDeferResponseData.data),
      _.clone,
    );
  } else if (unwrappedSubscriptionOrDeferResponseData?.data) {
    updatedResponse.body.data = _.merge(
      {},
      updatedResponse.body.data,
      unwrappedSubscriptionOrDeferResponseData.data,
    );
  } else if (
    unwrappedSubscriptionOrDeferResponseData?.data === null &&
    !updatedResponse.body.data
  ) {
    updatedResponse.body.data = null;
  }

  if (
    unwrappedSubscriptionOrDeferResponseData &&
    'incremental' in unwrappedSubscriptionOrDeferResponseData &&
    unwrappedSubscriptionOrDeferResponseData.incremental
  ) {
    updatedResponse =
      unwrappedSubscriptionOrDeferResponseData.incremental.reduce(
        (acc, data) =>
          updateMultipartResponse({
            currentResponse: acc,
            newResponse: { data, size: 0 }, // set size to 0, because the root already included the size of the incremental key
            extractExtensions,
            pushToHistory: false,
            shouldTerminate,
          }),
        updatedResponse,
      );
  }

  if (
    pushToHistory &&
    !_.isEqual(_.last(updatedResponse.history)?.data, updatedResponse.body)
  ) {
    updatedResponse.history = [
      ...updatedResponse.history,
      { data: updatedResponse.body, timestamp: Date.now() },
    ];
  }
  return updatedResponse;
}

export function handleApolloHeadersAndExtraction({
  processedHeaders,
  operationString,
  requestQueryPlan,
  showTraces,
  isMultipartSubscription,
  addContentType,
  requestConnectorsDebugging,
}: {
  processedHeaders: Record<string, string>;
  operationString: string;
  requestQueryPlan: boolean;
  showTraces: boolean;
  isMultipartSubscription: boolean;
  addContentType: boolean;
  requestConnectorsDebugging: boolean;
}) {
  // If we want to be able to modify headers we need to make a copy of them
  const processedHeadersWithApolloHeaders = processOperationHeaders(
    processedHeaders,
    operationString,
    isMultipartSubscription,
    addContentType,
  );

  function processApolloHeader(
    shouldInject: boolean,
    headerKey: string,
    validValues: [string, ...string[]],
  ) {
    const [userHeaderKey = headerKey, userDefinedHeaderValue] =
      Object.entries(processedHeadersWithApolloHeaders).find(
        ([key]) => key.toLowerCase() === headerKey.toLowerCase(),
      ) ?? [];

    if (shouldInject) {
      if (!userDefinedHeaderValue) {
        processedHeadersWithApolloHeaders[userHeaderKey] = validValues[0];
      } else if (!validValues.includes(userDefinedHeaderValue)) {
        throw new Error(
          `${userHeaderKey} is invalid, either remove the ${userHeaderKey} header and let Explorer handle setting it, or set it to '${validValues.join("' or '")}'`,
        );
      }
    }
    return !!userDefinedHeaderValue;
  }

  // Send header for request query plans from router
  const hasUserDefinedQueryPlanHeader = processApolloHeader(
    requestQueryPlan,
    'Apollo-Expose-Query-Plan',
    ['true', 'dry-run'],
  );

  // Send header for request connectors debugging from router
  const hasUserDefinedConnectorsDebuggingHeader = processApolloHeader(
    requestConnectorsDebugging,
    CONNECTORS_DEBUGGING_HEADER,
    ['true'],
  );

  // send headers for requesting traces from gateway
  const hasUserDefinedTracesHeader = processApolloHeader(
    showTraces,
    'apollo-federation-include-trace',
    [TRACE_KEY],
  );

  return {
    processedHeadersWithApolloHeaders,
    extractExtensions: ({
      body,
    }: {
      body: ExecutionResult<JSONValue | Record<string, unknown>, Extensions>;
    }) => {
      const routerQueryPlan = body.extensions?.apolloQueryPlan;
      const trace = body?.extensions?.[TRACE_KEY];
      const connectorsDebugging = body.extensions?.[CONNECTORS_DEBUGGING_KEY];
      if (body.extensions) {
        // remove query plan from extensions if not user requested
        const removeQueryPlanValue =
          requestQueryPlan && !hasUserDefinedQueryPlanHeader && routerQueryPlan;
        // If the user hasn't defined the header themselves, remove the trace from the response

        // eslint-disable-next-line no-param-reassign
        if (removeQueryPlanValue) delete body.extensions.apolloQueryPlan;

        // remove connectors debugging from extensions since displayed in debug panel
        const removeConnectorsDebuggingValue =
          !!body.extensions[CONNECTORS_DEBUGGING_KEY];
        if (removeConnectorsDebuggingValue)
          // eslint-disable-next-line no-param-reassign
          delete body.extensions[CONNECTORS_DEBUGGING_KEY];

        // remove traces from extensions if not user requested
        const removeTraceValue =
          showTraces && !hasUserDefinedTracesHeader && trace;
        // If the user hasn't defined the header themselves, remove the trace from the response
        // eslint-disable-next-line no-param-reassign
        if (removeTraceValue) delete body.extensions[TRACE_KEY];

        // if we requested data but the user didn't, delete extensions key
        if (
          (removeTraceValue ||
            removeQueryPlanValue ||
            removeConnectorsDebuggingValue) &&
          _.isEmpty(body.extensions)
        ) {
          // eslint-disable-next-line no-param-reassign
          delete body.extensions;
        }
      }

      return {
        trace: showTraces ? (trace ?? null) : undefined,
        queryPlan: requestQueryPlan ? (routerQueryPlan ?? null) : undefined,
        connectorsDebugging:
          requestConnectorsDebugging || hasUserDefinedConnectorsDebuggingHeader
            ? connectorsDebugging
            : undefined,
      };
    },
  };
}

export type OnResponseUpdate =
  | ((
      response: ResponseAndMetrics | CancelledResponse,
      shouldTerminate: boolean,
      unsubscribe?: () => void,
    ) => void)
  | undefined;

type PerformOperationParams<TOnResponseUpdate extends OnResponseUpdate> = {
  requestBody: OperationRequestBody;
  processedHeaders: Record<string, string>;
  /** GraphQL endpoint to make request to */
  url: string;
  includeCookies: boolean;
  abortSignal: AbortSignal | undefined;
  showTraces: boolean;
  requestQueryPlan: boolean;
  requestConnectorsDebugging: boolean;
  fileVariables?: FileVariable[] | undefined;
  /**
   * Multipurpose callback function:
   * 1. used for multipart (defer & subscription) responses each time a
   *    new chunk is received
   * 2. used for non multipart responses to show results in the response panel
   *
   * If this is passed, no response is returned from performOperation
   */
  onResponseUpdate: TOnResponseUpdate;
  isMultipartSubscription: boolean;
};

export type PerformOperationReturn<TOnResponseUpdate> =
  TOnResponseUpdate extends undefined
    ? ResponseAndMetrics | CancelledResponse
    : ResponseUpdatedInCallback;

export const performOperation = async <
  TOnResponseUpdate extends OnResponseUpdate,
>({
  requestBody,
  processedHeaders,
  url,
  includeCookies,
  abortSignal,
  showTraces,
  requestQueryPlan,
  fileVariables,
  onResponseUpdate,
  isMultipartSubscription,
  requestConnectorsDebugging,
}: PerformOperationParams<TOnResponseUpdate>): Promise<
  PerformOperationReturn<TOnResponseUpdate>
> => {
  const { processedHeadersWithApolloHeaders, extractExtensions } =
    handleApolloHeadersAndExtraction({
      processedHeaders,
      operationString: requestBody.query,
      requestQueryPlan,
      showTraces,
      isMultipartSubscription,
      // for multipart form requests, the content type needs to have `multipart/form-data`
      // and the boundary https://developer.mozilla.org/en-US/docs/Web/HTTP/Headers/Content-Type
      // the browser fills it in for us when we make it undefined,
      // since we don't know the boundary at this stage
      addContentType: !isMinLengthArray(1, fileVariables),
      requestConnectorsDebugging,
    });

  const startTime = Date.now();
  let response: Response;
  if (isMinLengthArray(1, fileVariables)) {
    const form = constructMultipartForm({
      fileVariables,
      requestBody,
    });

    response = await fetch(url, {
      signal: abortSignal,
      method: 'POST',
      headers: processedHeadersWithApolloHeaders,
      body: form,
      ...(includeCookies ? { credentials: 'include' } : {}),
    });
  } else {
    response = await fetch(url, {
      signal: abortSignal,
      method: 'POST',
      headers: processedHeadersWithApolloHeaders,
      body: JSON.stringify(requestBody),
      ...(includeCookies ? { credentials: 'include' } : {}),
    });
  }

  const contentType = response.headers?.get('content-type');
  const mimeType = contentType && new MIMEType(contentType);
  if (
    mimeType &&
    mimeType.type === 'multipart' &&
    mimeType.subtype === 'mixed'
  ) {
    const { observable, closeReadableStream } = readMultipartWebStream(
      response,
      mimeType,
    );

    const promise: Promise<PerformOperationReturn<TOnResponseUpdate>> =
      new Promise((resolve, reject) => {
        let currentResponseAccumulator = _.cloneDeep(DEFAULT_CURRENT_RESPONSE);
        let observableSubscription: Subscription;

        function resolveResponse(
          currentResponse: CurrentResponse,
          hasNext: boolean,
          shouldTerminate: boolean,
        ) {
          const nextResponse = buildResponse({
            ...currentResponse,
            bodyText: undefined,
            fallbackSize: currentResponse.size,
            startTime,
            response,
            hasNext,
          });
          if (onResponseUpdate) {
            onResponseUpdate(nextResponse, shouldTerminate, () => {
              closeReadableStream?.();
              observableSubscription.unsubscribe();
            });
            // if `onResponseUpdate` isn't passed in, that means we want to resolve the promise with all of the data.
            // this means waiting until `hasNext` is false to resolve the promise.
          } else if (!hasNext) {
            resolve(nextResponse as PerformOperationReturn<TOnResponseUpdate>);
          }
        }
        observableSubscription = observable.subscribe({
          next({ response: newResponse, shouldTerminate }) {
            const nextCurrentResponse = updateMultipartResponse({
              currentResponse: currentResponseAccumulator,
              newResponse,
              extractExtensions,
              shouldTerminate,
            });
            resolveResponse(
              nextCurrentResponse,
              !shouldTerminate,
              shouldTerminate,
            );

            // for subscriptions, we want to send the responses as they come in
            // but for defer and stream, we want to merge previous responses with the current one and send
            // the merged response.
            if (!isMultipartSubscription) {
              currentResponseAccumulator = nextCurrentResponse;
            }
          },
          error(err) {
            reject(err);
          },
          complete() {
            resolveResponse(currentResponseAccumulator, false, false);
          },
        });
      });

    if (onResponseUpdate) {
      return {
        type: 'response-updated-in-callback',
        status: response.status,
      } as PerformOperationReturn<TOnResponseUpdate>;
    } else {
      return promise;
    }
  } else {
    const bodyText = await response.text();
    let body: undefined | ExecutionResult<JSONObject, { [TRACE_KEY]?: string }>;
    try {
      body = JSON.parse(bodyText) as ExecutionResult<
        JSONObject,
        { [TRACE_KEY]?: string }
      >;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    }
    const { trace, queryPlan, connectorsDebugging } =
      (body && extractExtensions({ body })) ?? {};

    const responseAndMetrics = buildResponse({
      body,
      bodyText,
      fallbackSize: bodyText.length,
      startTime,
      response,
      hasNext: false,
      queryPlan,
      trace,
      connectorsDebugging,
    });

    if (onResponseUpdate) {
      onResponseUpdate(responseAndMetrics, true);
      return {
        type: 'response-updated-in-callback',
        status: response.status,
      } as PerformOperationReturn<TOnResponseUpdate>;
    } else {
      return responseAndMetrics as PerformOperationReturn<TOnResponseUpdate>;
    }
  }
};
